import { PageContainer, ProDescriptions, ProTable } from '@ant-design/pro-components';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Drawer, Popconfirm } from 'antd';
import { useRef, useState } from 'react';
import CreateOrganizationForm from '../components/CreateOrganizationForm';
import UpdateOrganizationExternalIdentityForm from '../components/UpdateOrganizationExternalIdentityForm';
import UpdateOrganizationForm from '../components/UpdateOrganizationForm';
import UpdateOrganizationOwnerForm from '../components/UpdateOrganizationOwnerForm';
import { queryOrganizations, updateOrganizationStatus } from '../services/organizations';

const ORGANIZATION_STATUS = {
  active: {
    text: '已激活',
    status: 'Success',
  },
  frozen: {
    text: '已冻结',
    status: 'Error',
  },
};

const Organizations = () => {
  const actionRef = useRef(null);
  const [currentOrganization, setCurrentOrganization] = useState();
  const { message } = App.useApp();
  const { mutateAsync: updateStatus, isPending: updatingStatus } = useMutation({
    mutationFn: updateOrganizationStatus,
    onSuccess: () => {
      actionRef.current?.reload?.();
      message.success('租户状态已更新');
    },
    onError: () => {
      message.error('租户状态更新失败，请重试');
    },
  });

  const reload = () => {
    actionRef.current?.reload?.();
  };

  const columns = [
    {
      title: '租户名称',
      dataIndex: 'name',
      render: (dom, record) => (
        <Button
          type="link"
          onClick={() => {
            setCurrentOrganization(record);
          }}
        >
          {dom}
        </Button>
      ),
    },
    {
      title: '租户标识',
      dataIndex: 'slug',
      copyable: true,
    },
    {
      title: 'Organization Id',
      dataIndex: 'id',
      copyable: true,
      search: false,
    },
    {
      title: '状态',
      dataIndex: 'status',
      valueEnum: ORGANIZATION_STATUS,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      valueType: 'date',
      search: false,
    },
    {
      title: '成员数',
      dataIndex: 'memberCount',
      valueType: 'digit',
      search: false,
      sorter: true,
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      valueType: 'date',
      search: false,
      sorter: true,
    },
    {
      title: '操作',
      valueType: 'option',
      render: (_, record) => {
        const nextStatus = record.status === 'active' ? 'frozen' : 'active';
        const nextStatusText = nextStatus === 'active' ? '激活' : '冻结';
        return [
          <UpdateOrganizationForm key="edit" reload={reload} values={record} />,
          <UpdateOrganizationOwnerForm key="owner" organization={record} reload={reload} />,
          <UpdateOrganizationExternalIdentityForm key="external" organization={record} />,
          <Popconfirm
            key="status"
            title={`${nextStatusText}租户`}
            description={`确认${nextStatusText}${record.name}？`}
            okText="确认"
            cancelText="取消"
            onConfirm={() =>
              updateStatus({
                data: {
                  id: record.id,
                  status: nextStatus,
                },
              })
            }
          >
            <Button type="link" loading={updatingStatus}>
              {nextStatusText}
            </Button>
          </Popconfirm>,
        ];
      },
    },
  ];
  const descriptionColumns = columns.filter((column) => column.valueType !== 'option');

  return (
    <PageContainer content="厂商管理员在这里创建租户、维护租户状态，并设置租户默认管理员。">
      <ProTable
        actionRef={actionRef}
        rowKey="id"
        headerTitle="租户列表"
        request={queryOrganizations}
        columns={columns}
        search={{
          labelWidth: 96,
        }}
        scroll={{
          x: 'max-content',
        }}
        toolBarRender={() => [<CreateOrganizationForm key="create" reload={reload} />]}
      />
      <Drawer
        open={Boolean(currentOrganization)}
        size={640}
        title={currentOrganization?.name}
        onClose={() => {
          setCurrentOrganization(undefined);
        }}
      >
        {currentOrganization && (
          <ProDescriptions
            column={2}
            request={async () => ({
              data: currentOrganization,
              success: true,
            })}
            columns={descriptionColumns}
          />
        )}
      </Drawer>
    </PageContainer>
  );
};

export default Organizations;
